import React, {
  Dispatch, SetStateAction, useContext, useEffect, useState,
} from 'react';
import { useIntercom } from 'react-use-intercom';
import { DropResult, ResponderProvided } from 'react-beautiful-dnd';
import {
  TaskItem, TaskItems, TaskOrderField, TasksPage, TaskTab, UserCenterPage,
} from '../../types/types';
import EmptyTaskTab from './EmptyTaskTab';
import DroppableTaskCardsHolder from './DroppableTaskCardsHolder';
import DraggableTaskCard from './DraggableTaskCard';
import { dbBatchUpdateTaskOrder } from '../../../database/firebaseTasksAPI';
import { logEventDragAndDropTasks } from '../../../utils/analytics/eventLogger';
import { filterTasksByTaskTab } from '../../../utils/tasks/tasksUtils';
import { PENDING } from '../../../utils/enums';
import SpinnerFlexCenter from '../css-spinner/SpinnerFlexCenter';
import { AuthContext } from '../../../App';

interface Props {
  taskItems: TaskItems,
  taskTab: TaskTab,
  setSection?: Dispatch<SetStateAction<UserCenterPage>>
  page: TasksPage,
  taskOrderField: TaskOrderField,
  setCreateTaskModalOpen: Dispatch<SetStateAction<boolean>>,
  disableCalendarSelect?: boolean,
  disableAssigneeSelect?: boolean,
  disableGoToMeeting?: boolean,
}

const TaskTabContent = ({
  taskItems, taskTab, page, setCreateTaskModalOpen, taskOrderField, setSection,
  disableAssigneeSelect, disableCalendarSelect, disableGoToMeeting,
}: Props) => {
  const [displayTasks, setDisplayTasks] = useState<TaskItem[]>([]);
  const { trackEvent } = useIntercom();
  const authState = useContext(AuthContext);

  useEffect(() => {
    const tasks = filterTasksByTaskTab(taskItems.tasks, taskTab);
    setDisplayTasks(tasks);
  }, [taskItems]);

  // eslint-disable-next-line no-unused-vars
  const onDragEnd = (result: DropResult, provided: ResponderProvided) => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;

    const reorderedTasks = reorder(
      displayTasks,
      result.source.index,
      result.destination.index,
    );

    setDisplayTasks(reorderedTasks);
    dbBatchUpdateTaskOrder(reorderedTasks, taskOrderField);
    logEventDragAndDropTasks(authState.userId, page, trackEvent);
  };

  const reorder = (taskArray: TaskItem[], source: number, destination: number) => {
    const result = [...taskArray];
    const [removedItem] = result.splice(source, 1);
    result.splice(destination, 0, removedItem);
    result.forEach((task, index) => {
      result[index].order[taskOrderField] = index;
    });
    return result;
  };

  const compareTasksOrderIndex = (
    firstTask: TaskItem, secondTask: TaskItem,
  ) => firstTask.order[taskOrderField] - secondTask.order[taskOrderField];

  if (taskItems.resolvedState === PENDING) {
    return (
      <SpinnerFlexCenter size={40} />
    );
  }

  if (displayTasks.length === 0) {
    return (
      <EmptyTaskTab setCreateTaskModalOpen={setCreateTaskModalOpen} />
    );
  }

  return (
    <DroppableTaskCardsHolder onDragEnd={onDragEnd}>
      {displayTasks.sort(compareTasksOrderIndex).map((task, index) => (
        <DraggableTaskCard
          key={task.taskId}
          task={task}
          setSection={setSection}
          index={index}
          disableCalendarSelect={disableCalendarSelect}
          disableAssigneeSelect={disableAssigneeSelect}
          disableGoToMeeting={disableGoToMeeting}
        />
      ))}
    </DroppableTaskCardsHolder>
  );
};

TaskTabContent.defaultProps = {
  setSection: () => {},
  disableCalendarSelect: false,
  disableAssigneeSelect: false,
  disableGoToMeeting: false,
};

export default TaskTabContent;
